import { apiClient } from "../../api/apiClient.js";

/**
 * P164: the MOU import cases a job-side บริษัทจัดหางาน runs, over the real
 * /api/backoffice/worker-line/plcs/{plcOrganizationId}/mou-cases API.
 *
 * Every path is built from the owning company's id, exactly as the server
 * routes them: the company is what every permission decision here is about, so
 * a call that could not name it would be a call the server could not authorize.
 *
 * Nothing in this module knows what the eight steps ARE. Their titles, their
 * summaries and their document checklists all arrive on the case itself, so the
 * browser can never end up showing a process the server is not enforcing.
 */

function casesPath(plcOrganizationId) {
  return `/backoffice/worker-line/plcs/${encodeURIComponent(plcOrganizationId)}/mou-cases`;
}

function casePath(plcOrganizationId, mouCaseId) {
  return `${casesPath(plcOrganizationId)}/${encodeURIComponent(mouCaseId)}`;
}

function requireCase(plcOrganizationId, mouCaseId, caller) {
  if (!plcOrganizationId || !mouCaseId) {
    throw new Error(`${caller} requires a plcOrganizationId and a mouCaseId`);
  }
}

/** Every case this company has ever opened, newest first. */
export function listMouCases(plcOrganizationId) {
  if (!plcOrganizationId) {
    throw new Error("listMouCases requires a plcOrganizationId");
  }
  return apiClient.get(casesPath(plcOrganizationId));
}

/** One case in full: its own fields, all eight steps, and every attached document. */
export function getMouCase(plcOrganizationId, mouCaseId) {
  requireCase(plcOrganizationId, mouCaseId, "getMouCase");
  return apiClient.get(casePath(plcOrganizationId, mouCaseId));
}

/** Opens a new case. The server creates its eight steps; there is no separate call to make them. */
export function createMouCase(plcOrganizationId, form) {
  if (!plcOrganizationId) {
    throw new Error("createMouCase requires a plcOrganizationId");
  }
  return apiClient.post(casesPath(plcOrganizationId), {
    // The country of a round is fixed when it is opened - every step after this
    // is filed with that country's own offices.
    countryCode: form.countryCode ?? null,
    employerName: form.employerName ?? null,
    originCompanyName: form.originCompanyName ?? null,
    // An empty box means "not known yet", not zero workers - sending 0 would
    // record a demand for nobody.
    workerCountRequested: form.workerCountRequested === "" || form.workerCountRequested == null
      ? null
      : Number(form.workerCountRequested),
    jobTypeLabel: form.jobTypeLabel ?? null,
    note: form.note ?? null,
  });
}

/**
 * Edits the case's own fields. Only the keys present in `patch` are sent, which
 * is what makes a field left alone stay alone: the server treats an absent key
 * as unchanged and an empty string as cleared.
 */
export function updateMouCase(plcOrganizationId, mouCaseId, patch) {
  requireCase(plcOrganizationId, mouCaseId, "updateMouCase");
  return apiClient.patch(casePath(plcOrganizationId, mouCaseId), patch);
}

/**
 * Removes one round. A soft delete on the server - the row and its audit trail
 * survive - so this returns the refreshed LIST rather than nothing, and the grid
 * behind the confirmation is right without a second request.
 */
export function deleteMouCase(plcOrganizationId, mouCaseId) {
  requireCase(plcOrganizationId, mouCaseId, "deleteMouCase");
  return apiClient.delete(casePath(plcOrganizationId, mouCaseId));
}

/** Moves one of the eight steps - its status, office reference number, filing date and note. */
export function updateMouCaseStep(plcOrganizationId, mouCaseId, stepNumber, patch) {
  requireCase(plcOrganizationId, mouCaseId, "updateMouCaseStep");
  if (!stepNumber) {
    throw new Error("updateMouCaseStep requires a stepNumber");
  }
  return apiClient.patch(`${casePath(plcOrganizationId, mouCaseId)}/steps/${encodeURIComponent(stepNumber)}`, patch);
}

/**
 * Attaches one document.
 *
 * `stepNumber` pins it to a step and is omitted for a case-level document;
 * `documentType` is the label it is filed under, and filing it under a step's
 * own checklist label is what ticks that row off.
 */
export function uploadMouCaseDocument(plcOrganizationId, mouCaseId, file, { stepNumber = null, documentType = null } = {}) {
  requireCase(plcOrganizationId, mouCaseId, "uploadMouCaseDocument");
  if (!file) {
    throw new Error("uploadMouCaseDocument requires a file");
  }

  const formData = new FormData();
  formData.append("document", file);
  if (stepNumber != null) {
    formData.append("stepNumber", String(stepNumber));
  }
  if (documentType) {
    formData.append("documentType", documentType);
  }

  return apiClient.postForm(`${casePath(plcOrganizationId, mouCaseId)}/documents`, formData);
}

/** Soft-deletes one attached document. The file and its audit trail survive on the server. */
export function deleteMouCaseDocument(plcOrganizationId, mouCaseId, documentId) {
  requireCase(plcOrganizationId, mouCaseId, "deleteMouCaseDocument");
  if (!documentId) {
    throw new Error("deleteMouCaseDocument requires a documentId");
  }
  return apiClient.delete(`${casePath(plcOrganizationId, mouCaseId)}/documents/${encodeURIComponent(documentId)}`);
}

/**
 * One attached document's real bytes. Read through the authenticated client and
 * never as a bare href - the route carries no session token of its own, so a
 * plain link to it would 401.
 */
export function getMouCaseDocumentBlob(plcOrganizationId, mouCaseId, documentId) {
  requireCase(plcOrganizationId, mouCaseId, "getMouCaseDocumentBlob");
  if (!documentId) {
    throw new Error("getMouCaseDocumentBlob requires a documentId");
  }
  return apiClient.getBlob(`${casePath(plcOrganizationId, mouCaseId)}/documents/${encodeURIComponent(documentId)}/file`);
}
